import React from "react";
import { Link } from "react-router-dom";
import { TrendUp, TrendDown, CaretRight } from "@phosphor-icons/react";
import { useTokenPrice } from "../hooks/useTokenPrice";

function fmtUsdPrice(p) {
  if (p == null) return "—";
  if (p >= 1) return `$${p.toFixed(4)}`;
  if (p >= 0.01) return `$${p.toFixed(5)}`;
  if (p >= 0.0001) return `$${p.toFixed(6)}`;
  return `$${p.toExponential(2)}`;
}

function fmtCompactUsd(v) {
  if (v == null) return "—";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(2)}K`;
  if (v >= 1) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(2)}`;
}

/**
 * Compact live MFSMAI price pill for the header (desktop + mobile).
 * Clickable — opens /about so users can see the full token info.
 */
export default function TokenPricePill({ variant = "header" }) {
  const price = useTokenPrice();
  if (!price?.available) return null;

  const up = (price.change_24h_pct ?? 0) >= 0;
  const trend = up ? "text-profit" : "text-loss";
  const TrendIcon = up ? TrendUp : TrendDown;

  if (variant === "header") {
    return (
      <Link
        to="/about"
        data-testid="token-price-pill"
        title={`Pro entry: ${fmtCompactUsd(price.pro_threshold_usd)} (5,000 MFSMAI)\nMarket cap: ${fmtCompactUsd(price.market_cap_usd)}`}
        className="hidden sm:inline-flex items-center gap-2 px-2.5 py-1 border border-white/10 hover:border-edge/60 bg-zinc-950/40 transition-colors group"
      >
        <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500 group-hover:text-edge">
          MFSMAI
        </span>
        <span className="font-mono text-xs tabular-nums text-zinc-100">
          {fmtUsdPrice(price.price_usd)}
        </span>
        <span className={`inline-flex items-center gap-0.5 font-mono text-[10px] tabular-nums ${trend}`}>
          <TrendIcon size={10} weight="fill" />
          {Math.abs(price.change_24h_pct ?? 0).toFixed(2)}%
        </span>
      </Link>
    );
  }

  // Big stat-card variant for /about and dashboard
  return (
    <div
      data-testid="token-price-card"
      className="border border-edge/40 bg-edge/5 p-4 sm:p-5"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-edge">
            MFSMAI · Live Price
          </div>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="font-display text-2xl sm:text-3xl font-semibold tabular-nums text-white">
              {fmtUsdPrice(price.price_usd)}
            </span>
            <span className={`inline-flex items-center gap-1 font-mono text-sm tabular-nums ${trend}`}>
              <TrendIcon size={14} weight="fill" />
              {price.change_24h_pct >= 0 ? "+" : ""}
              {(price.change_24h_pct ?? 0).toFixed(2)}% · 24h
            </span>
          </div>
        </div>
        <a
          href={price.pair_url || `https://pancakeswap.finance/swap?outputCurrency=${price.token_contract}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="trade-on-dex-btn"
          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-edge hover:text-white"
        >
          Trade on {price.dex || "PancakeSwap"} <CaretRight size={11} />
        </a>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <Stat label="Market Cap" value={fmtCompactUsd(price.market_cap_usd)} />
        <Stat label="Liquidity" value={fmtCompactUsd(price.liquidity_usd)} />
        <Stat label="24h Volume" value={fmtCompactUsd(price.volume_24h_usd)} />
        <Stat
          label="Pro entry"
          value={fmtCompactUsd(price.pro_threshold_usd)}
          sub="5,000 MFSMAI"
          accent="text-edge"
        />
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
        {label}
      </div>
      <div className={`mt-0.5 font-mono tabular-nums ${accent || "text-zinc-100"}`}>
        {value}
      </div>
      {sub && <div className="text-[9px] font-mono text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}
